import { IssueTimelineEventTypeEnum } from '@issuemanager/constants';
import { z } from 'zod';

const stringParameterSchema = z.object({
  value: z.string(),
  type: z.enum(['string']),
});

const nullableStringParameterSchema = z.object({
  value: z.string().nullable(),
  type: z.enum(['string', 'null']),
});

const timestampParameterSchema = z.object({
  value: z.string(),
  type: z.enum(['timestamp']),
});

const attachmentsParameterSchema = z.object({
  value: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      hbDriveId: z.string(),
      size: z.number(),
      createdAt: z.string(),
      addedBy: z.string(),
    }),
  ),
  type: z.enum(['array']),
});

const timelineParametersSchema = z.object({
  timestamp: timestampParameterSchema,
  actorName: stringParameterSchema,
  actorInitials: stringParameterSchema.optional(),
  issueCode: stringParameterSchema.optional(),
  issueOwnerId: stringParameterSchema.optional(),
  issueOwnerName: stringParameterSchema.optional(),
  issueDescription: stringParameterSchema.optional(),
  issueStatus: stringParameterSchema.optional(),
  actionTitle: stringParameterSchema.optional(),
  actionId: stringParameterSchema.optional(),
  issueComment: stringParameterSchema.optional(),
  discardMessage: stringParameterSchema.optional(),
  reason: stringParameterSchema.optional(),
  editedField: stringParameterSchema.optional(),
  editedFrom: nullableStringParameterSchema.optional(),
  editedTo: nullableStringParameterSchema.optional(),
  attachments: attachmentsParameterSchema.optional(),
  relationshipObjectId: stringParameterSchema.optional(),
  relationshipType: stringParameterSchema.optional(),
  relationshipObjectName: stringParameterSchema.optional(),
  relationshipObjectUrl: stringParameterSchema.optional(),
});

const baseTimelineSchema = z.object({
  id: z.string(),
  issue_id: z.string(),
  event_type: z.nativeEnum(IssueTimelineEventTypeEnum),
  actor_user_id: z.string(),
  event_at: z.union([z.number(), z.string()]).transform((n) => new Date(n)),
  body_data: z.object({
    v: z.string(),
    parameters: timelineParametersSchema,
  }),
});

export const rawIssueTimelineSchema = baseTimelineSchema;
export type RawIssueTimelineSchema = z.infer<typeof rawIssueTimelineSchema>;

export const issueTimelineEventDatabaseSchema = baseTimelineSchema.transform(
  (data) => ({
    id: data.id,
    eventType: data.event_type,
    actorId: data.actor_user_id,
    eventAt: data.event_at,
    bodyData: data.body_data,
  }),
);

export type IssueTimelineEventEntity = z.infer<
  typeof issueTimelineEventDatabaseSchema
>;

export const minimalIssueTimelineSchema = rawIssueTimelineSchema
  .pick({
    id: true,
    event_type: true,
    actor_user_id: true,
    event_at: true,
    body_data: true,
  })
  .transform((data) => ({
    id: data.id,
    eventType: data.event_type,
    actorId: data.actor_user_id,
    eventAt: data.event_at,
    bodyData: data.body_data,
  }));

export type MinimalIssueTimelineEntity = z.infer<
  typeof minimalIssueTimelineSchema
>;
